import React, {useCallback, useContext, useEffect, useState} from 'react';
import {connect, DispatchProp} from 'react-redux'
import {Link, useNavigate} from "react-router-dom";
import classNames from "classnames";
import {useTranslation, withTranslation} from "react-i18next";
import {Button, Navbar} from "react-bootstrap";
import {ReactComponent as LeftIco} from "../../../images/icons/arrow-left.svg";
import {RootState} from "../../../reducers";
import {getSession, selectLocale} from "../../services/Session/reducer";
import {getStoreConfig} from "../../services/Store/reducer";
import IframeUtils from "../../utils/IframeUtils";
import CartStickyComponent from "../../components/Cart/CartStickyComponent";
import CartItemsComponent from "../../components/Cart/components/CartItems/CartItemsComponent";
import CartSummaryDataComponent from "../../components/Cart/CartSummaryDataComponent";
import FetchData from "../../components/Fetch/FetchData";
import CartRulesComponent from "../../components/Cart/CartRulesComponent";
import CartClientDataComponent from "../../components/Cart/CartClientDataComponent";
import CartPaymentComponent from "../../components/Cart/CartPaymentComponent";
import CartSubmitComponent from "../../components/Cart/CartSubmitComponent";
import CartCustomFieldComponent from "../../components/Cart/CartCustomFieldComponent";
import {useCart} from "../../services/Cart/cartService";
import Analytics from "../../services/Common/Analytics/Analytics";
import Pixel from "../../services/Common/Analytics/Pixel";
import StringHelper from "../../services/Common/StringHelper";
import CartHelper from "../../services/Cart/Helper/CartHelper";
import {isValidTaxIdNo} from "../../utils/taxIdNo";
import AppConfig from "../../services/AppConfig";
import {usePrefix} from "../../services/Prefix";
import {getCart} from "../../services/Cart/reducer";
import CartDeliveryInfoComponent from "../../components/Cart/CartDeliveryInfoComponent";
import CartTypeComponent from "../../components/Cart/CartTypeComponent";
import CartPickDateComponent from "../../components/Cart/CartPickDateComponent";
import CartRegisterComponent from "../../components/Cart/CartRegisterComponent";
import CartCommentComponent from "../../components/Cart/components/CartCommentComponent";
import {getUser} from "../../services/User/reducer";
import CartCashbackComponent from "../../components/Cart/CartCashbackComponent";
import {CartState} from "../../services/Cart/types";
import CartStickyContextPage from "../Contexts/CartStickyContextPage";
import handleException from "../../services/Api/handleException";
import LoadingContainer from "../../components/Common/LoadingContainer";
import CartUtils from "../../services/Cart/CartUtils";
import StoreUtils from "../../services/Store/StoreUtils";
import {ApiError, DineInType, OrderType} from "../../services/Api/types";
import {addFlash} from "../../services/Notifications/actions";
import ResponsiveComponent from "../../components/Common/ResponsiveComponent";
import CartVouchersComponent from "../../components/Cart/CartPromotionsSection";
import {SessionState} from "../../services/Session/types";
import {useSessionService} from "../../services/Session/sessionService";
import UserUtils from "../../services/User/UserUtils";
import CartUserDataComponent from "../../components/Cart/CartUserDataComponent";
import CartSubmitInfoComponent from "../../components/Cart/CartSubmitInfoComponent";
import CartTipComponent from "../../components/Cart/CartTipComponent";
import {
    getAvailabilities,
    getCategories,
    getItemGroups,
    getItems,
    getMenus,
    getPromotions
} from "../../services/Menu/reducer";
import VoucherProductsModal from "../../components/Modal/VoucherProductsModal";
import {VoucherProductsContext} from "../../services/VoucherProducts/VoucherProductsContext";
import {FormatAddressString} from "go-core/components/Formatters/FormatAddress";


interface OwnProps {
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;
const fillCartWithContact = (cart: CartState, session: SessionState) => {
    let contact = session.contact ? {...session.contact} : {};
    return {
        ...cart,
        contact: contact,
        comment: session.cart_comment
    }
}

const CartPage = (props: Props) => {
    const [errors, setErrors] = useState<any[]>([]);
    const cart = props.cart;
    const [data, setData] = useState<CartState>(cart);
    const navigate = useNavigate();
    const {t} = useTranslation();
    let cartService = useCart();
    const prefix = usePrefix();
    const sessionService = useSessionService();
    const [onBlurData, setOnBlurData] = useState<CartState>(data);
    const {
        showVoucherProductsModal,
        setShowVoucherProductsModal,
    } = useContext(VoucherProductsContext);
    const [tableNumberModal, setTableNumberModal] = useState(false);
    const isMultilevelMenu = StoreUtils.isMultilevelMenu(props.config);

    const updateData = async (updater: ((prev: CartState) => CartState), fieldErrorsToClear?: string) => {
        setData((prev) => {
            const newData = updater(prev);

            sessionService.updateCartData({
                cart_comment: newData.comment || undefined,
                contact: newData.contact || undefined,
            } as SessionState);

            return newData;
        });

        if (fieldErrorsToClear) {
            removeInputError(fieldErrorsToClear);
        }
    }

    const validateTaxIdNo = (taxIdNo?: string) => {
        const isValid = isValidTaxIdNo(taxIdNo);
        setErrors((prev) => [
            ...prev.filter(error => error.field !== "taxIdNo"),
            ...(isValid ? [] : [{field: "taxIdNo", code: "taxidno_not_valid", message: ""}])
        ]);

        return isValid;
    }

    const saveOrder = async () => {
        if (cart.type === OrderType.DINE_IN && props.session?.dine_in_type === DineInType.TABLE && !CartUtils.getCartNumber(props.session)) {
            setTableNumberModal(true);
            return;
        }

        if (!validateTaxIdNo(data.tax_id_no)) {
            setTimeout(scrollToErrorSection);
            return;
        }

        const availablePaymentMethods = CartHelper.getAvailablePaymentMethods(props.config?.ordering?.payment_methods ? props.config?.ordering?.payment_methods : [], cart.type);
        const paymentMethodId = CartHelper.getDefaultPaymentMethodId(data.payment_method_id, availablePaymentMethods);
        const total = cart.total;
        const orderData = {
            address: props.session.address,
            contact: {
                name: data.contact.name,
                email: data.contact.email,
                phone: data.contact.phone,
            },
            comment: StringHelper.emptyOrValue(data.comment),
            tax_id_no: data.tax_id_no?.trim(),
            custom_fields: data.custom_fields,
            payment_method_id: paymentMethodId,
            language: props.locale ? props.locale.toUpperCase() : undefined,
            type: cart.type,
            number: CartUtils.getCartNumber(props.session),
            pickup_at: cart.pickup_at,
            accept_marketing: cart.accepted_rules.includes('acceptMarketing')
        }

        try {
            const newData = await cartService.update(orderData) as any;
            if (total.amount !== newData.total_money.amount) {
                props.dispatch(addFlash('error', [{
                    code: t("constraints.internal.order_changed_price")
                }] as ApiError[]));
                return;
            }
            await pay(paymentMethodId, data.payment_settings, cart.accepted_rules);
        } catch (error: any) {
            const errors = handleException(error);
            setErrors(errors);
            scrollToErrorSection();
        }
    }
    const scrollToErrorSection = () => {
        const headerOffset = 80; // Adjust this value based on your desired offset
        const cartElement = document.querySelectorAll('.cart');
        if (cartElement.length > 0) {
            const lastModal = cartElement[cartElement.length - 1];
            let errorElement = lastModal.querySelector('.is-invalid');
            if (!errorElement) {
                errorElement = lastModal.querySelector('.alert-danger');
            }
            if (errorElement) {
                const targetTop = errorElement.getBoundingClientRect().top + window.scrollY - headerOffset;
                window.scrollTo({
                    top: targetTop,
                    behavior: 'smooth'
                });
            }
        }
    };
    const pay = async (paymentMethodId: any, paymentSettings: any, acceptedRules: any) => {

        let data = {...paymentSettings};
        data.payment_method_id = paymentMethodId;
        data.accepted_rules = acceptedRules;
        const response = await cartService.pay(data) as any;
        if (response.data && response.data.status === "WAITING_FOR_ACCEPTED") {
            await cartService.clean();
            navigate(AppConfig.getLinkWithPrefix(`/summary/${response.data.id}/${response.data.token}`, prefix));
        } else {
            if (response.redirect_url !== undefined && response.redirect_url) {
                IframeUtils.redirect(response.redirect_url);
            }
        }
    }
    useEffect(() => {
        if (cart.id) {
            Analytics.beginCheckout(cart);
            Pixel.initiateCheckout(cart);
        }
    }, [cart.id])

    const handleUpdateDataOnSessionChange = useCallback(() => {
        if (cart.id) {
            setData(() => fillCartWithContact(cart.id !== data.id ? cart : data, props.session));
            const fieldsToCheck = ["contact.name", "contact.phone", "contact.email"];

            fieldsToCheck.forEach(field => removeInputError(field))

        }
    }, [cart.id, props.session.contact?.email, props.session.contact?.name, props.session.contact?.phone])

    useEffect(() => {
        handleUpdateDataOnSessionChange();
    }, [handleUpdateDataOnSessionChange])


    if (!props.cart.initialized) return (
        <LoadingContainer/>
    );
    const updateCustomFieldValue = (slug: any, value: any) => {
        if (cart.custom_fields === undefined) {
            cart.custom_fields = {};
        }
        cart.custom_fields[slug] = value;
        setData((prevState) => ({
            ...prevState,
            custom_fields: {
                ...prevState.custom_fields,
                [slug]: value
            }
        }));
    }

    const getGoBackLink = () => {
        if (StoreUtils.isHomepageMenu(props.config)) {
            return AppConfig.getLinkWithPrefix("/", prefix);
        }

        if (isMultilevelMenu) {
            return AppConfig.getLinkWithPrefix("/menu", prefix);
        }

        return AppConfig.getLinkWithPrefix("/", prefix);
    }

    const backToHome = () => {
        navigate(getGoBackLink(), {replace: true});
    }

    const removeInputError = (field: string) => {
        const errorIndex = errors.findIndex(error => error.field === field);

        if (errorIndex > -1) {
            const newErrors = JSON.parse(JSON.stringify(errors));
            newErrors.splice(errorIndex, 1);
            setErrors(newErrors);
        }
    }

    const handleInputBlur = (field: string, value?: string) => {
        const previousValue = field.split(".").reduce((acc: any, part) => acc?.[part], onBlurData);

        if (value && previousValue !== value) {
            removeInputError(field);
        }
        setOnBlurData(data);
    }

    const getShopInfoTitle = () => {
        let result = props.config?.name ?? "";
        const address = FormatAddressString(props.config?.address);

        if (address) {
            result += `, ${address}`
        }

        return result;
    }

    const renderAfterFetch = () => {
        return (
            <div className="cart-page">
                <CartStickyContextPage config={props.config}>
                    <Navbar className={"sticky-top navbar-back"}>
                        <div className={"navbar-brand-container"}>
                            <Link className={"btn btn-back navbar-back-btn"} to={getGoBackLink()}>
                                <LeftIco/>
                                <div className={"multiline d-flex flex-column"}>
                                    <h5>{t("modules.cart.field.order.title")}</h5>
                                    <p className="text-muted">{getShopInfoTitle()}</p>
                                </div>
                            </Link>
                        </div>

                    </Navbar>
                    <div>
                        {!(StoreUtils.isOrderingReadonly(props.config)) && (
                            <CartStickyComponent stickySummary withoutHeader placement="CART" menuId={undefined}/>
                        )}
                        {
                            showVoucherProductsModal && <VoucherProductsModal show={showVoucherProductsModal}
                                                                              onHide={() => setShowVoucherProductsModal(false)}
                                                                              menus={props.menus}
                                                                              promotions={props.promotions}
                                                                              cart={props.cart}
                                                                              availabilities={props.availabilities}
                                                                              session={props.session}
                                                                              isStorePaused={StoreUtils.isPaused(props.config)}
                                                                              usePromotionItemModal
                                                                              items={props.items}
                                                                              categories={props.categories}
                                                                              locale={props.locale}
                                                                              config={props.config}
                            />
                        }

                        <div className={classNames('body-container body-container-cart')}>

                            <div className="cart cart-container">
                                <div className={"container-fluid"}>
                                    <CartDeliveryInfoComponent/>
                                </div>
                                {cart.id ? (
                                    <>
                                        <>
                                            <div className={"card-cart card-cart-items"}>
                                                <div className={"container-fluid"}>
                                                    <div className={"card-cart-body"}>
                                                        <CartItemsComponent
                                                            items={cart.items}></CartItemsComponent>
                                                    </div>
                                                </div>
                                            </div>
                                        </>

                                        {(!AppConfig.isNativeMobile()) && (
                                            <>
                                                <CartRegisterComponent errors={errors}></CartRegisterComponent>
                                            </>
                                        )}
                                        {!StoreUtils.isOrderingReadonly(props.config) && (
                                            <>
                                                {UserUtils.isLogged(props.user) ? (
                                                    <CartUserDataComponent data={data} errors={errors}
                                                                           handleInputBlur={handleInputBlur}/>
                                                ) : (
                                                    <CartClientDataComponent data={data} onChange={updateData}
                                                                             errors={errors}
                                                                             handleInputBlur={handleInputBlur}/>
                                                )}
                                            </>
                                        )}

                                        {!StoreUtils.isOrderingReadonly(props.config) && (
                                            <CartCustomFieldComponent data={data}
                                                                      onChange={updateData}
                                                                      onTaxIdNoBlur={validateTaxIdNo}
                                                                      errors={errors}/>
                                        )}

                                        <CartTypeComponent errors={errors}
                                                           setTableNumberModal={setTableNumberModal}
                                                           tableNumberModal={tableNumberModal}
                                                           onChangeCustomField={updateCustomFieldValue}
                                                           handleInputBlur={handleInputBlur}
                                                           setErrors={setErrors}
                                        />

                                        <CartPickDateComponent errors={errors}
                                                               onChangeCustomField={updateCustomFieldValue}></CartPickDateComponent>

                                        {!StoreUtils.isOrderingReadonly(props.config) && (
                                            <>
                                                <CartPaymentComponent errors={errors} onChange={updateData}
                                                                      data={data}/>
                                                <CartCommentComponent data={data} onChange={updateData}
                                                                      errors={errors}/>
                                                {CartUtils.isTipEnabled(props.config) && (
                                                    <CartTipComponent data={data} errors={errors}/>
                                                )}
                                                <CartVouchersComponent updateErrors={setErrors}/>
                                            </>
                                        )}
                                        <div className={"card-cart"}>

                                            <div className={"container-fluid"}>
                                                <ResponsiveComponent small={
                                                    <h5 className={"card-cart-header"}>
                                                        {t("modules.cart.field.summary.title")}
                                                    </h5>
                                                } large={
                                                    <>
                                                        {
                                                            props.config?.ordering?.rules && props.config.ordering.rules.length > 0 ?
                                                                <h5 className={"card-cart-header"}>
                                                                    {t("modules.cart.field.summary.title")}
                                                                </h5> : <></>
                                                        }
                                                    </>
                                                }/>
                                                <CartCashbackComponent className="mb-3"/>
                                                <div className={"card-cart-body"}>
                                                    <div className={"mb-3 cart-summary-wrapper"}>
                                                        <CartSummaryDataComponent/>
                                                    </div>
                                                    {!StoreUtils.isOrderingReadonly(props.config) && (
                                                        <div className="cart-rules mb-3">
                                                            <CartRulesComponent errors={errors}
                                                                                clearErrors={() => removeInputError("acceptedRules")}/>
                                                        </div>
                                                    )}
                                                    {!StoreUtils.isOrderingReadonly(props.config) ? (
                                                        <div className="btn-pay-container mb-3">
                                                            <CartSubmitComponent saveOrder={saveOrder}/>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            className={"w-100 btn btn-spinner btn-primary btn-submit"}
                                                            type="button"
                                                            disabled={true}>
                                                            {t("modules.cart.action.call_waiter_to_order")}
                                                        </button>
                                                    )}
                                                    <CartSubmitInfoComponent/>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className={"container-fluid"}>
                                        <p className="fw-bolder">
                                            {t("modules.cart.field.empty_cart.title")}
                                        </p>
                                        <Button className={"btn btn-primary"} onClick={backToHome}>
                                            {t("modules.cart.action.back_to_home.title")}
                                        </Button>
                                    </div>
                                )}


                            </div>
                        </div>
                    </div>
                </CartStickyContextPage>
            </div>
        )
    }
    return (
        <FetchData menus={true} order={true}>
            {renderAfterFetch()}
        </FetchData>
    )
}


const mapStateToProps = (state: RootState) => ({
    session: getSession(state.session),
    config: getStoreConfig(state.config),
    cart: getCart(state.cart),
    user: getUser(state.user),
    items: getItems(state.menu),
    menus: getMenus(state.menu),
    availabilities: getAvailabilities(state.menu),
    promotions: getPromotions(state.menu),
    itemGroups: getItemGroups(state.menu),
    locale: selectLocale(state.session),
    categories: getCategories(state.menu)
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(CartPage));
